import React, { useEffect, useState, useMemo, useCallback } from 'react';
import QuadralTimeline from "@quadral/quadral-component-common/dist/components/timeline";
import QuadralTypography from "@quadral/quadral-component-common/dist/components/typography";
import QuadralContainer from "@quadral/quadral-component-common/dist/components/container";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function Activities({
    activityRecords,
    formatDate,
    assistanceType,
    setAssistanceType,
    setActivityOrder,
    roles,
    participationId,
    deviationId,
    quadralToast,
    updateRecord,
    showLoader,
    hideLoader,
    getTypeIcon,
    getTypeColor,
    ...props}) {

    const mailCodeAliases = useMemo(() => ({
        C01: "Lancement de l'enquête - Invitation à la connexion",
        M01: "Lancement de l'enquête - Invitation à la connexion",
        M02: "Relance - Connexion à l'enquête",
        M05: "Relance - Remplissez votre formulaire d'enquête",
        M06: "Relance - Pièces justificatives manquantes",
        M10: "Accusé de reception - Nous analysons vos pièces justificatives",
        S10: "Accusé de reception - Nous analysons vos pièces justificatives",
        C20: "Notification - Compléments attendus",
        M20: "Notification - Compléments attendus",
        S20: "Notification - Compléments attendus",
        M25: "Confirmation - Réception des compléments demandés",
        S25: "Confirmation - Réception des compléments demandés",
        C30: "Confirmation - Votre participation a été contrôlée",
        M30: "Confirmation - Votre participation a été contrôlée",
        C40: "Pré-mise en demeure - Votre dossier est incomplet",
        M40: "Pré-mise en demeure - Votre dossier est incomplet",
        S40: "Pré-mise en demeure - Votre dossier est incomplet",
        C41: "Pré-mise en demeure - Nous n'avons eu aucune réponse de votre part",
        M41: "Pré-mise en demeure - Nous n'avons eu aucune réponse de votre part",
        C50: "Mise en demeure - Votre dossier est incomplet",
        C51: "Mise en demeure - Nous n'avons eu aucune réponse de votre part",
    }), []);

    const motiveOptions = useMemo(() => [
        {   label: "-- Sélectionner un motif --",    value: "selectmotive", },
		{   label: "Global - Compréhension de l'enquête ou d'une relance",    value: "Global_Comprehension_de_l_enquete_ou_d_une_relance", },
		{   label: "Global - Le client ne dispose pas d'une pièce que nous lui avons demandé",    value: "Global_Le_client_ne_dispose_pas_d_une_piece_que_nous_lui_avons_demande", },
        {   label: "Global - Souhait de confirmer bonne réception du dossier",    value: "Global_Souhait_de_confirmer_bonne_reception_du_dossier", },
        {   label: "Papier - Usage - Accompagnement renseignement formulaire",    value: "Papier_Usage_Accompagnement_renseignement_formulaire", },
        {   label: "Télédéclaration - Usage - Accompagnement parcours en ligne (accès/saisie/validation)",    value: "Teledeclaration_Usage_Accompagnement_parcours_en_ligne", },
        {   label: "Télédéclaration - Usage - Souhait de réponse au format papier",      value: "Teledeclaration_Usage_Souhait_de_reponse_au_format_papier"     },
        {   label: "Télédéclaration - Usage - Téléchargement des pièces",      value: "Teledeclaration_Usage_Telechargement_des_pieces"     },
        {   label: "Télédéclaration - Problème technique",      value: "Teledeclaration_Probleme_technique"     },
        {   label: "Divers - Autres motifs",      value: "Divers_Autres_motifs"     },
    ], []);

    const[timeline, setTimeline] = useState([]);
    // const[documentLink, setDocumentLink]=useState([]);
    // const[selectedPreview, setSelectedPreview] = useState(false);
    // const[previewName, setPreviewName]=useState('')

    function isLink(string){
        try {
            new URL(string);
            return true;
        } catch (error) {
            return false;
        }
    }

    // function preview(title, page) {
    //     setPreviewName(`${title}`);
    //     setSelectedPreview(true);
    //     setDocumentLink(page);
    // }

    function extractContentAndAuthor(text) {
        if(!text) return ["", ""];

        const separator = "par l’utilisateur";
        const index = text.toLowerCase().lastIndexOf(separator.toLowerCase());

        if (index === -1) {
            return [text.trim(), ""];
        }

        const message = text.slice(0, index).trim();

        let author = text
            .slice(index + separator.length)
            .trim();

        if (!author || author.toLowerCase() === "null") {
            author = "";
        }

        return [message, author];
    }
 
    function extractMailCode(subject, normalizedType) {
        let code = null;
        if (typeof subject !== "string") return null;
        if (normalizedType === "task"){
            const match = subject.split(" - ");
            code = match ? match[1] : null;
            if (code == null) {
                const split = subject.split(" ");
                const match = split[2].slice(-3);
                code = match ? match : null;
            }
        } else if (normalizedType === "email"){
            const match = subject.slice(-3);
            code = match ? match : null;
        }
        return code;
    }

    const makeTitle = useCallback((element) => {
        if (!element.subject) {
            return "Non renseigné";
        }
        const normalizedType = String(element.type).trim().toLowerCase();
        if (normalizedType === "task" || normalizedType === "email") {
            const code = extractMailCode(element.subject, normalizedType);
            if (code && mailCodeAliases[code]) return mailCodeAliases[code];
        }
        return motiveOptions.find(opt => opt.value === element.subject)?.label || element.subject;
    }, [mailCodeAliases, motiveOptions]);

    useEffect(() => {
        const newTimeline = activityRecords?.activities?.map((element) => ({
            title: makeTitle(element),
            content: 
                isLink(element.description) ? 
                    <QuadralTypography variant='tableBodyWide'>
                        <a href={element.description} target="_blank" rel="noopener noreferrer">
                            Cliquez ici pour ouvrir le courrier dans un nouvel onglet
                        </a>
                    </QuadralTypography>
                : extractContentAndAuthor(element.description)[0],
            time: element.creationDate ? (() => {
                const [date, time] = element.creationDate.split("T");
                const timeFormatted = time.substring(0, 5);
                return `${formatDate(date)} - ${timeFormatted}`;
            })() : "inconnu",
            origin: element.description ? (() => {
                const user = extractContentAndAuthor(element.description)[1];
                return user ? user : null;
            })() : null,
            icon: <FontAwesomeIcon icon={getTypeIcon(element.type)} />,
            iconColor: getTypeColor(element.type),
        }))

        setTimeline(newTimeline);
    }, [activityRecords, mailCodeAliases, motiveOptions, makeTitle, formatDate, getTypeIcon, getTypeColor]);

    return (
        <>
        <QuadralTypography variant='timelineOrigin' sx={{textTransform: 'none', fontSize: '0.75rem', fontWeight: '700'}}>{activityRecords?.totalCount} activité{activityRecords?.totalCount > 1 ? 's' : ''} trouvée{activityRecords?.totalCount > 1 ? 's' : ''}</QuadralTypography>
        <QuadralContainer orientation='row' style={{transition: 'all 0.3s'}}>
            <QuadralContainer orientation='column'
                style={{
                    maxWidth: "100%",
                    minWidth: '55%',
                    transition: "max-width 0.5s ease-in-out"
            }}>
                <QuadralTimeline timelineLength={activityRecords?.totalCount} timeline={timeline}/>
            </QuadralContainer>
            {/* <QuadralContainer orientation='column'
                className="supporting-doc-list" style={{
                display: selectedPreview ? "flex" : "none",
                flexDirection: "column",
                marginBottom: "1rem",
                transition: "max-width 0.5s ease-in-out",
                minHeight: selectedPreview ? "740px" : '0px',
                minWidth: "20rem"
                }}>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '10px' }}>
                    <div className='panel-title detail-title c-pb-0'
                        style={{ margin: '7px 0 7px 0' }}>{previewName}</div>
                </div>
                <div className="preview-close" style={{ backgroundColor: 'grey' }}>
                    <button className="preview-close-btn" style={{ backgroundColor: 'grey', color: 'white', border: 'none' }} onClick={() => setSelectedPreview(null)} title="Fermer la prévisualisation">
                        <i className="fa-regular fa-xmark fa-f2" style={{ backgroundColor: 'grey' }} />
                    </button>
                </div>
                {documentLink && documentLink?.length > 0 ? (
                    <object aria-label='Prévisualisation du document' type='application/pdf' width="100%" className="h-100 w-100"
                        data={`${documentLink}#zoom=FitH`} />
                ) : (
                    <>
                    <div>
                        Erreur lors de la récupération du courrier
                    </div>
                    </>
                )}
            </QuadralContainer> */}
        </QuadralContainer>
        </>
    );
}